import { advanceStatus, type StatusTransitions } from "@/shared/utils/statusProgress";

import { PayinRuntimeStatus } from "../services/paymentsPayin.service";

/**
 * Mirror of coloca-nest `PAYIN_TRANSITIONS` (`src/coloca/payments/state/payin-transitions.ts`): the only
 * moves the backend ever makes. A verdict (REJECTED, FAILED, REVERSED) is final; CONFIRMED can still be
 * REVERSED or FAILED.
 */
const PAYIN_TRANSITIONS: StatusTransitions<PayinRuntimeStatus> = {
  [PayinRuntimeStatus.CREATED]: [
    PayinRuntimeStatus.PENDING,
    PayinRuntimeStatus.FAILED,
    PayinRuntimeStatus.REJECTED,
  ],
  [PayinRuntimeStatus.PENDING]: [
    PayinRuntimeStatus.CONFIRMED,
    PayinRuntimeStatus.REJECTED,
    PayinRuntimeStatus.FAILED,
  ],
  [PayinRuntimeStatus.CONFIRMED]: [PayinRuntimeStatus.REVERSED, PayinRuntimeStatus.FAILED],
  [PayinRuntimeStatus.REJECTED]: [],
  [PayinRuntimeStatus.FAILED]: [],
  [PayinRuntimeStatus.REVERSED]: [],
};

/**
 * The payin status to show after one more reading, from either live channel: a paid payin never goes back to
 * showing how to pay it. See `advanceStatus`.
 */
export function advancePayinStatus(
  current: PayinRuntimeStatus | null,
  incoming: string | null | undefined,
): PayinRuntimeStatus | null {
  return advanceStatus(PAYIN_TRANSITIONS, current, incoming);
}

/** What a payin screen shows: the instrument while the payin is `open`, an outcome once it has one. */
export type PayinOutcome = "open" | "paid" | "refused" | "reversed";

/**
 * Every final status hides the instrument, REVERSED included: a reversed payin was credited and taken back,
 * and offering its QR or CLABE again would invite a second payment with no notice of the first. A status this
 * build does not know keeps the instrument, as before.
 */
export function payinOutcome(status: PayinRuntimeStatus | null): PayinOutcome {
  switch (status) {
    case PayinRuntimeStatus.CONFIRMED:
      return "paid";
    case PayinRuntimeStatus.REVERSED:
      return "reversed";
    case PayinRuntimeStatus.REJECTED:
    case PayinRuntimeStatus.FAILED:
      return "refused";
    default:
      return "open";
  }
}
